import type { MessageKey } from "./en";

/**
 * Russian (Cyrillic). Covers the player page only for now; missing keys fall back to English.
 * Informal but polite: «вы», lowercase. Placeholders such as {percent} must stay exactly as in en.ts.
 */
export const ru: Partial<Record<MessageKey, string>> = {
  "app.name": "Goalhorns Online",
  "app.loading": "Загрузка…",
  "app.retry": "Попробовать снова",

  "common.save": "Сохранить",
  "common.cancel": "Отмена",
  "common.close": "Закрыть",
  "common.delete": "Удалить",
  "common.restore": "Восстановить",
  "common.edit": "Изменить",
  "common.copy": "Копировать",
  "common.copied": "Скопировано",
  "common.share": "Поделиться",
  "common.add": "Добавить",
  "common.remove": "Убрать",
  "common.rename": "Переименовать",
  "common.duplicate": "Дублировать",
  "common.open": "Открыть",
  "common.create": "Создать",
  "common.confirm": "Подтвердить",
  "common.none": "Нет",
  "common.active": "Активный",
  "common.inactive": "Неактивный",
  "common.locked": "Заблокировано",
  "common.unlocked": "Разблокировано",
  "common.deleted": "Удалено",
  "common.filterActive": "Активные",
  "common.filterDeleted": "Удалённые",
  "common.role.owner": "Владелец",
  "common.role.member": "Участник",

  "lang.label": "Язык",
  "lang.en": "EN",
  "lang.rs": "RS",
  "lang.ru": "RU",

  "roster.number": "№ {number}",
  "roster.noNumber": "Без номера",
  "link.shareUnsupported": "Общий доступ в этом браузере недоступен. Используйте «Копировать».",

  "player.lockedBanner": "Ваш звукорежиссёр пока заблокировал изменения",
  "player.invalidTitle": "Эта ссылка больше не действует",
  "player.invalidText": "Попросите звукорежиссёра прислать новую.",
  "player.photo": "Ваше фото",
  "player.photoAlt": "Ваше фото",
  "player.photoAdd": "Добавить фото",
  "player.photoReplace": "Заменить ваше фото",
  "player.photoUploadLabel": "Загрузить фото",
  "player.photoReplaceLabel": "Заменить фото",
  "player.photoFile": "Файл фото",
  "player.photoLockedNote": "Фото установил звукорежиссёр",
  "player.photoSaved": "Фото сохранено.",
  "player.uploadingPhoto": "Загрузка фото: {percent}%",
  "player.consent":
    "Я имею право делиться этим фото; игрокам младше 16 лет нужно согласие родителей",
  "player.song": "Ваша песня",
  "player.songFile": "Файл песни",
  "player.songUpload": "Загрузить песню",
  "player.songReplace": "Заменить песню",
  "player.songHint": "MP3, M4A или AAC, до 20 МБ.",
  "player.consentHint": "Отметьте согласие, чтобы загрузить фото.",
  "player.songLockedNote": "Песню установил звукорежиссёр",
  "player.songNone": "Песни пока нет. Загрузите её, затем выберите отрывок (до 30 секунд).",
  "player.songSaved": "Песня загружена.",
  "player.songTooShort": "Эта песня короче 3 секунд.",
  "player.preparing": "Подготовка файла…",
  "player.uploading": "Загрузка песни: {percent}%",
  "player.waveformLoading": "Загрузка волновой формы…",
  "player.waveformError": "Не удалось загрузить волновую форму.",
  "player.play": "Воспроизвести отрывок",
  "player.stop": "Стоп",
  "player.regionLength": "{seconds} с",
  "player.regionSaved": "Ваш отрывок сохранён. Звукорежиссёр получит его при следующем обновлении.",
  "player.lockedTitle": "Заблокировал звукорежиссёр",
  "player.lockedExplain": "Свой отрывок вы по-прежнему можете прослушать.",
  "player.invalidFull": "Эта ссылка больше не работает. Попросите новую у звукорежиссёра.",
  "player.sectionLabel": "Ваш отрывок",

  "error.unknown": "Что-то пошло не так. Попробуйте ещё раз.",
  "error.network_error": "Нет соединения. Проверьте сеть и попробуйте ещё раз.",
  "error.bad_response": "Сервер вернул неожиданный ответ.",
  "error.unknown_error": "Что-то пошло не так. Попробуйте ещё раз.",
  "error.internal_error": "Внутренняя ошибка сервера. Попробуйте позже.",
  "error.http_error": "Не удалось выполнить запрос.",
  "error.validation_error": "Некоторые введённые данные некорректны.",
  "error.not_found": "Элемент не найден.",
  "error.unauthorized": "Войдите в систему, чтобы продолжить.",
  "error.forbidden": "У вас нет прав на это действие.",
  "error.csrf_origin": "Запрос пришёл с другого сайта и был отклонён.",
  "error.access_required": "Не удалось подтвердить права администратора.",
  "error.rate_limited": "Слишком много попыток. Подождите немного и попробуйте снова.",
  "error.locked": "Это заблокировано. Сначала разблокируйте конфигурацию или человека.",
  "error.photo_locked": "Фото заблокировано оператором.",
  "error.song_locked": "Песня заблокирована оператором.",
  "error.consent_required": "Подтвердите согласие перед загрузкой фото.",
  "error.quota_exceeded": "Лимит для этого рабочего пространства исчерпан.",
  "error.too_large": "Файл слишком большой.",
  "error.ext_not_allowed": "Этот тип файла не разрешён.",
  "error.media_kind_mismatch": "Этот файл не того типа, который нужен.",
  "error.checksum_mismatch": "Файл не совпал с контрольной суммой. Попробуйте загрузить снова.",
  "error.size_mismatch": "Размер загруженного файла не совпал. Попробуйте загрузить снова.",
  "error.empty_body": "Файл пустой.",
  "error.length_required": "В загрузке не указана длина.",
  "error.duration_required": "У песни не указана длительность.",
  "error.song_required": "Для отрывка сначала нужна песня.",
  "error.region_invalid": "Этот отрывок не подходит для данной песни.",
  "error.hotkey_duplicate": "Это сочетание клавиш уже занято другим слотом.",
  "error.hotkey_invalid": "Некорректное сочетание клавиш.",
  "error.hotkey_reserved": "Это сочетание клавиш зарезервировано настольным приложением.",
  "error.person_duplicate": "Человек может быть в конфигурации только один раз.",
  "error.person_not_found": "Человека из списка больше не существует.",
  "error.export_invalid": "Эту конфигурацию нельзя экспортировать в таком виде. Проверьте слоты.",
  "error.owner_protected": "Членство владельца нельзя изменить.",
  "error.cannot_remove_self": "Нельзя удалить самого себя.",
  "error.not_invited": "У этой учётной записи нет приглашения. Попросите владельца прислать его.",
  "error.invite_invalid": "Приглашение недействительно, истекло или уже использовано.",
  "error.invite_email_mismatch":
    "Войдите с адресом электронной почты, на который отправлено приглашение.",
  "error.email_unverified": "Адрес электронной почты вашего аккаунта Google не подтверждён.",
  "error.oauth_not_configured": "Вход через Google пока не настроен.",
  "error.oauth_state_invalid": "Время входа истекло. Начните заново.",
  "error.dev_login_forbidden": "Вход для разработки работает только на localhost.",
};
